import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { ORDER_STATUSES, LINE_STATUSES } from '../firebase/schema';
import { isSampleOrder, sampleLineReceiveFields } from './sampleOrder';
import { lineAwaitingSupplierOrder, lineHasBeenOrdered } from './lineCutStatus';
import { syncOrderPaymentIfInvoiceable } from './orderPayment';
import { spPrintBlockReasonForCustomerLines } from './spLicenseStore';

/** DB / UI line type for design-only work (outside production). */
export const isDesignOnlyLineType = (type) => {
  const t = String(type || '').toLowerCase();
  return t === 'design_only' || t === 'design only';
};

/** Line fields when design work is considered done for production/invoicing purposes. */
export const designOnlyLineCompletionFields = () => ({
  status: LINE_STATUSES.COMPLETED,
  printed: true
});

/**
 * When every line is COMPLETED, move the order to Ready To Invoice (billing queue).
 * No-op if order is already terminal or not all lines are complete.
 */
export const maybeSetOrderReadyToInvoiceIfAllLinesComplete = async (db, orderId) => {
  if (!orderId) return { skipped: true };

  const linesSnap = await getDocs(query(collection(db, 'orderLines'), where('orderId', '==', orderId)));
  const lines = linesSnap.docs.map((d) => d.data());
  if (lines.length === 0) return { skipped: true };

  const allCompleted = lines.every((l) => l.status === LINE_STATUSES.COMPLETED);
  if (!allCompleted) return { skipped: true, reason: 'not_all_completed' };

  const orderRef = doc(db, 'orders', orderId);
  const orderSnap = await getDoc(orderRef);
  if (!orderSnap.exists()) return { skipped: true };

  const current = orderSnap.data()?.status;
  if (
    current === ORDER_STATUSES.READY_TO_INVOICE ||
    current === ORDER_STATUSES.COMPLETED ||
    current === ORDER_STATUSES.CANCELLED
  ) {
    return { skipped: true, current };
  }

  await updateDoc(orderRef, {
    status: ORDER_STATUSES.READY_TO_INVOICE,
    updatedAt: serverTimestamp()
  });
  await syncOrderPaymentIfInvoiceable(db, orderId);
  return { updated: true };
};

/**
 * Recompute and persist an order's aggregate status from all of its lines.
 * Used after IN/CUT updates outside the Lines table flow.
 */
export const syncOrderStatusFromLines = async (db, orderId) => {
  if (!orderId) return null;

  const linesSnap = await getDocs(query(collection(db, 'orderLines'), where('orderId', '==', orderId)));
  const orderLines = linesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const orderSnap = await getDoc(doc(db, 'orders', orderId));
  const orderData = orderSnap.exists() ? orderSnap.data() : null;
  const sample = isSampleOrder(orderData);
  const productionLines = orderLines.filter((l) => !isDesignOnlyLineType(l.type));
  const total = productionLines.length;
  if (total === 0) return null;

  const inCount = productionLines.filter((l) => l.isIn === true).length;
  const cutAndInCount = productionLines.filter((l) => l.isIn === true && l.isCut === true).length;

  const orderRef = doc(db, 'orders', orderId);
  if (!orderSnap.exists()) return null;

  const currentStatus = orderData?.status;
  // Be permissive for active orders so status can recover from legacy/manual values.
  // Only skip terminal states that should not be auto-regressed.
  const terminalStatuses = [
    ORDER_STATUSES.READY_TO_INVOICE,
    ORDER_STATUSES.COMPLETED,
    ORDER_STATUSES.CANCELLED,
    ORDER_STATUSES.WAITING_ON_REPRINT
  ];
  const canUpdate = !terminalStatuses.includes(currentStatus);

  if (!canUpdate) return { skipped: true, currentStatus, reason: 'terminal_status' };

  // Order/lines still in the ordering queue — cutting film/screen must not mark the job ordered.
  if (currentStatus === ORDER_STATUSES.READY_TO_ORDER) {
    return { skipped: true, currentStatus, reason: 'ready_to_order' };
  }
  const awaitingSupplierOrder = productionLines.some((l) => lineAwaitingSupplierOrder(l.status));
  if (awaitingSupplierOrder) {
    return { skipped: true, currentStatus, reason: 'lines_awaiting_supplier_order' };
  }

  let newOrderStatus;
  if (sample) {
    // Blanks only — received when all lines are IN (no print stage).
    if (inCount === total) {
      newOrderStatus = ORDER_STATUSES.RECEIVED;
    } else if (inCount > 0) {
      newOrderStatus = ORDER_STATUSES.PARTIALLY_RECEIVED;
    } else {
      newOrderStatus = ORDER_STATUSES.ORDERED;
    }
  } else if (cutAndInCount === total) {
    newOrderStatus = ORDER_STATUSES.READY_TO_PRINT;
  } else if (cutAndInCount > 0) {
    newOrderStatus = ORDER_STATUSES.PARTIALLY_READY_TO_PRINT;
  } else if (inCount === total) {
    newOrderStatus = ORDER_STATUSES.RECEIVED;
  } else if (inCount > 0) {
    newOrderStatus = ORDER_STATUSES.PARTIALLY_RECEIVED;
  } else {
    const allLinesOrderedOrLater = productionLines.every((l) => lineHasBeenOrdered(l.status));
    if (!allLinesOrderedOrLater) {
      return { skipped: true, currentStatus, reason: 'lines_not_all_ordered' };
    }
    newOrderStatus = ORDER_STATUSES.ORDERED;
  }

  if (newOrderStatus !== currentStatus) {
    await updateDoc(orderRef, {
      status: newOrderStatus,
      updatedAt: serverTimestamp()
    });
  }

  return { currentStatus, newOrderStatus };
};

/**
 * When every production line on a sample order is IN, complete lines and move order to Ready To Invoice
 * (triggers the "ready for pickup" customer email via Cloud Functions).
 */
export const maybeFinalizeSampleOrderAfterReceive = async (db, orderId) => {
  if (!orderId) return { skipped: true };

  const orderSnap = await getDoc(doc(db, 'orders', orderId));
  if (!orderSnap.exists() || !isSampleOrder(orderSnap.data())) {
    return { skipped: true, reason: 'not_sample' };
  }

  const linesSnap = await getDocs(query(collection(db, 'orderLines'), where('orderId', '==', orderId)));
  const lines = linesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const productionLines = lines.filter((l) => !isDesignOnlyLineType(l.type));
  if (productionLines.length === 0) return { skipped: true, reason: 'no_lines' };

  const allIn = productionLines.every((l) => l.isIn === true);
  if (!allIn) return { skipped: true, reason: 'not_all_in' };

  const blockReason = await spPrintBlockReasonForCustomerLines(
    db,
    orderSnap.data()?.customerId,
    productionLines
  );
  if (blockReason) {
    if (typeof window !== 'undefined') window.alert(blockReason);
    return { skipped: true, reason: 'sp_license' };
  }

  await Promise.all(
    linesSnap.docs.map((lineDoc) => {
      const data = lineDoc.data();
      if (isDesignOnlyLineType(data.type)) return Promise.resolve();
      if (data.status === LINE_STATUSES.COMPLETED && data.isIn === true) return Promise.resolve();
      return updateDoc(lineDoc.ref, {
        ...sampleLineReceiveFields(),
        countedInAt: data.countedInAt || new Date(),
        updatedAt: serverTimestamp()
      });
    })
  );

  return maybeSetOrderReadyToInvoiceIfAllLinesComplete(db, orderId);
};

